const CM_PER_INCH = 2.54;
const LB_PER_KG = 2.2046226218;

export const MIN_HEIGHT_CM = 100;
export const MAX_HEIGHT_CM = 220;
export const MIN_WEIGHT_KG = 30;
export const MAX_WEIGHT_KG = 300;

export const MIN_HEIGHT_FEET = 3;
export const MAX_HEIGHT_FEET = 7;
export const MIN_WEIGHT_LBS = Math.ceil(MIN_WEIGHT_KG * LB_PER_KG * 10) / 10;
export const MAX_WEIGHT_LBS = Math.floor(MAX_WEIGHT_KG * LB_PER_KG * 10) / 10;

export const usesMetricMeasurements = (locale) => {
  const normalized = String(locale || "ja")
    .toLowerCase()
    .split(/[-_]/)[0];
  // The English MMT flow uses US customary display units. All currently
  // supported non-English locales use metric display values.
  return normalized !== "en";
};

export const centimetersToFeetAndInches = (centimeters) => {
  const value = Number(centimeters);
  if (!Number.isFinite(value) || value <= 0) {
    return { feet: "", inches: "" };
  }
  const totalInches = Math.round(value / CM_PER_INCH);
  return {
    feet: String(Math.floor(totalInches / 12)),
    inches: String(totalInches % 12),
  };
};

export const feetAndInchesToCentimeters = (feet, inches) => {
  const feetValue = Number(feet);
  const inchesValue = Number(inches);
  if (
    !Number.isFinite(feetValue) ||
    !Number.isFinite(inchesValue) ||
    feetValue < 0 ||
    inchesValue < 0 ||
    inchesValue >= 12 ||
    feetValue * 12 + inchesValue <= 0
  ) {
    return null;
  }
  return Number(((feetValue * 12 + inchesValue) * CM_PER_INCH).toFixed(1));
};

export const isHeightCentimetersInRange = (centimeters) => {
  const value = Number(centimeters);
  return (
    Number.isFinite(value) && value >= MIN_HEIGHT_CM && value <= MAX_HEIGHT_CM
  );
};

export const isWeightKilogramsInRange = (kilograms) => {
  const value = Number(kilograms);
  return (
    Number.isFinite(value) && value >= MIN_WEIGHT_KG && value <= MAX_WEIGHT_KG
  );
};

export const isWeightPoundsInRange = (pounds) => {
  const value = Number(pounds);
  return (
    Number.isFinite(value) && value >= MIN_WEIGHT_LBS && value <= MAX_WEIGHT_LBS
  );
};

export const kilogramsToPounds = (kilograms) => {
  const value = Number(kilograms);
  if (!Number.isFinite(value) || value <= 0) return "";
  return String(Number((value * LB_PER_KG).toFixed(1)));
};

export const poundsToKilograms = (pounds) => {
  const value = Number(pounds);
  if (!Number.isFinite(value) || value <= 0) return null;
  // Extra precision prevents visible round-trip drift (for example 135 lb
  // reopening as 134.9 lb after being stored in kilograms).
  return Number((value / LB_PER_KG).toFixed(3));
};

export const hydrateHeightWeightFields = (height, weight, locale) => {
  const metric = usesMetricMeasurements(locale);
  const imperialHeight = centimetersToFeetAndInches(height);
  return {
    height: metric ? height || "" : "",
    heightFeet: metric ? "" : imperialHeight.feet,
    heightInches: metric ? "" : imperialHeight.inches,
    weight: metric ? weight || "" : kilogramsToPounds(weight),
  };
};

export const toStoredHeightWeight = (fields, locale) => {
  if (usesMetricMeasurements(locale)) {
    return { height: fields.height, weight: fields.weight };
  }
  return {
    height: feetAndInchesToCentimeters(fields.heightFeet, fields.heightInches),
    weight: fields.weight ? poundsToKilograms(fields.weight) : "",
  };
};

export const getHeightWeightValidation = (fields, locale = "ja") => {
  const metric = usesMetricMeasurements(locale);
  const metricHeightError =
    fields.height !== "" && !isHeightCentimetersInRange(fields.height);
  const imperialHeightComplete =
    fields.heightFeet !== "" && fields.heightInches !== "";
  const imperialHeightCm = imperialHeightComplete
    ? feetAndInchesToCentimeters(fields.heightFeet, fields.heightInches)
    : null;
  const imperialHeightError =
    imperialHeightComplete && !isHeightCentimetersInRange(imperialHeightCm);
  const heightError = metric ? metricHeightError : imperialHeightError;
  const weightError =
    fields.weight !== "" &&
    !(metric
      ? isWeightKilogramsInRange(fields.weight)
      : isWeightPoundsInRange(fields.weight));
  const hasHeight = metric ? fields.height !== "" : imperialHeightComplete;
  const hasWeight = fields.weight !== "";

  return {
    metric,
    heightError,
    weightError,
    hasHeight,
    hasWeight,
    isValid: hasHeight && hasWeight && !heightError && !weightError,
  };
};
